import axios from 'axios';
import runtimeConfig from '../../../runtime-config';

export const ATTEMPT_KEY = 'th-shop.checkout-attempt.v1';
const API = runtimeConfig.REACT_APP_API_URI;

export function readAttempt() {
  let raw;
  try { raw = window.localStorage.getItem(ATTEMPT_KEY); }
  catch (_) { throw new Error('Browser storage is unavailable. Enable it before starting payment.'); }
  if (!raw) return null;
  let value;
  try { value = JSON.parse(raw); } catch (_) { /* validated below */ }
  if (!value || value.version !== 1 || !/^[a-f0-9]{64}$/.test(value.token) ||
      !/^[a-f0-9]{64}$/.test(value.fingerprint) || !['stripe', 'paypal'].includes(value.method)) {
    throw new Error('Your saved checkout cannot be read. Please contact the shop before paying again.');
  }
  return value;
}

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      if (value[key] !== undefined) result[key] = canonical(value[key]);
      return result;
    }, {});
  }
  return value;
}

export async function fingerprint(payload) {
  if (!window.crypto || !window.crypto.subtle) throw new Error('This browser cannot securely save your checkout. Please use a current browser.');
  const bytes = new TextEncoder().encode(JSON.stringify(canonical(payload)));
  const hash = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash), n => n.toString(16).padStart(2, '0')).join('');
}

export function checkoutPayload({ customer, items, cartTotal, shipping, payment }, method) {
  const details = customer.details || {};
  return {
    customer: { name: details.name, email: details.email },
    basket_items: items,
    shipping: shipping.method === 'delivery'
      ? { method: 'delivery', delivery_address: shipping.delivery_address, deliveries: shipping.deliveries, cost: shipping.cost }
      : { method: 'collection', collection_address: shipping.collection_address, collection_option: shipping.collection_option, cost: shipping.cost },
    payment: { type: method, amount: Number(cartTotal) + Number(shipping.cost), billing: payment.billing_address },
  };
}

export async function ensureAttempt(payload) {
  const hash = await fingerprint(payload);
  let saved = readAttempt();
  if (saved) {
    if (saved.fingerprint !== hash || saved.method !== payload.payment.type) {
      throw new Error('A different checkout is unfinished. Reload this page to check or cancel it before starting another.');
    }
    return saved;
  }
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  saved = { version: 1, token: Array.from(bytes, n => n.toString(16).padStart(2, '0')).join(''),
    fingerprint: hash, method: payload.payment.type };
  try { window.localStorage.setItem(ATTEMPT_KEY, JSON.stringify(saved)); }
  catch (_) { throw new Error('Your checkout could not be saved. Enable browser storage before paying.'); }
  assertCurrentAttempt(saved);
  return saved;
}

export function assertCurrentAttempt(attempt) {
  const current = readAttempt();
  if (!current || current.token !== attempt.token) throw new Error('Your checkout changed in another tab. Reload to check its payment status.');
}

export function clearAttempt(attempt) {
  assertCurrentAttempt(attempt);
  window.localStorage.removeItem(ATTEMPT_KEY);
}

export async function startCheckout(payload) {
  const attempt = await ensureAttempt(payload);
  assertCurrentAttempt(attempt);
  const response = await axios.post(API + '/orders', { ...payload, checkout_token: attempt.token });
  if (response.data.status !== 'success' || !response.data.data) throw new Error(response.data.message || 'Unable to save your order. Your checkout is retained.');
  return { order: response.data.data, attempt };
}

export async function matchesBasket(attempt, payload) {
  const current = typeof payload === 'function' ? payload : () => payload;
  const snapshot = JSON.stringify(canonical(current()));
  const hash = await fingerprint(JSON.parse(snapshot));
  return attempt.fingerprint === hash && JSON.stringify(canonical(current())) === snapshot;
}

